import { Link } from 'found';
import PropTypes from 'prop-types';
import React from 'react';
import { Helmet } from 'react-helmet-async';
import { useIntl } from 'react-intl';
import { graphql, useFragment } from 'react-relay';
import { ButtonGroup, ButtonToolbar } from 'reactstrap';

import Loading from 'src/components/Loading';
import { APIConfig } from 'src/config';
import Breadcrumbs from 'src/enosikit/components/Breadcrumbs';
import Heading from 'src/enosikit/components/Heading';
import getFullAddressString from 'src/util/address';
import {
  FEATURE_INHIBIT_COMMUNITY_TRADES,
  FEATURE_INHIBIT_CONTRACTED_TRADES,
  FEATURE_INHIBIT_NOMINATED_TRADES,
} from 'src/util/constants';

import TradeRuleActiveContracted from './ContractedTradeRules';
import TradeRuleActiveProposed from './ProposedTradeRules';
import TradeRuleActiveNominated from './NominatedTradeRules';
import TradeRuleActiveCommunity from './CommunityTradeRules';
import TradeRuleActiveResidual from './ResidualTradeRules';

const TradeRuleActiveFragment = graphql`
  fragment TradeRuleActive_property on Property {
    id
    title
    address {
      line1
      line2
      city
      state
      postcode
      gps {
        latitude
        longitude
      }
    }
    ...CommunityTradeRules_property
    ...ContractedTradeRules_property
    ...NominatedTradeRules_property
    ...ProposedTradeRules_property
    ...ResidualTradeRules_property
  }
`;

/**
 * TradeRuleActive functional component displays active trade rules for a property.
 * @param {object} props - Component props
 * @param {object} props.property - Property data from GraphQL fragment
 * @param {object} props.router - React Router object providing navigation capabilities
 * @returns {React.ReactElement} The TradeRuleActive component
 */
function TradeRuleActive(props) {
  if (!props || Object.keys(props).length === 0) {
    return <Loading />;
  }

  const intl = useIntl();

  let { property = null } = props;

  if (!property) {
    return <Loading />;
  }

  property = useFragment(TradeRuleActiveFragment, property);

  const { address, id, title } = property;

  // i18n variables
  const allTradeRulesLabel = intl.formatMessage({ id: 'trade_rule.trade_rule_active.all_trade_rules.label', defaultMessage: 'All trade rules' });
  const propertySettingsLabel = intl.formatMessage({ id: 'trade_rule.trade_rule_active.property_settings.label', defaultMessage: 'Property Settings' });
  const pageTitle = intl.formatMessage(
    { id: 'trade_rule.trade_rule_active.page.title', defaultMessage: 'Enosi - Property: {propertyTitle}' },
    { propertyTitle: title },
  );
  const propertiesBreadcrumb = intl.formatMessage({ id: 'trade_rule.trade_rule_active.properties.breadcrumb', defaultMessage: 'Properties' });
  const tradeRulesBreadcrumb = intl.formatMessage({ id: 'trade_rule.trade_rule_active.trade_rules.breadcrumb', defaultMessage: 'Trade Rules' });
  const tradeRulesTitle = intl.formatMessage({ id: 'trade_rule.trade_rule_active.trade_rules.title', defaultMessage: 'Trade Rules' });

  const renderPropertyControls = () => (
    <ButtonToolbar>
      <ButtonGroup className="ms-2">
        <Link to={`/properties/${id}/trade-rules`} className="btn btn-darken">
          {allTradeRulesLabel}
        </Link>
      </ButtonGroup>

      <ButtonGroup className="ms-2">
        <Link to={`/properties/${id}/settings`} className="btn btn-darken">
          {propertySettingsLabel}
        </Link>
      </ButtonGroup>
    </ButtonToolbar>
  );

  const addr = getFullAddressString(address);

  return (
    <>
      <Helmet>
        <meta charSet="utf-8" />
        <title>{pageTitle}</title>
      </Helmet>

      <Breadcrumbs
        breadcrumbs={[
          {
            name: propertiesBreadcrumb, path: '/properties',
          },
          { name: title, path: `/properties/${id}` },
          { name: tradeRulesBreadcrumb },
        ]}
      />
      <Heading
        title={title}
        subtitle={addr}
        context={tradeRulesTitle}
        controls={renderPropertyControls()}
      />
      {!APIConfig().feature(FEATURE_INHIBIT_CONTRACTED_TRADES) && (
        <TradeRuleActiveContracted property={property} />
      )}
      {!APIConfig().feature(FEATURE_INHIBIT_NOMINATED_TRADES) && (
        <>
          <TradeRuleActiveProposed property={property} />
          <TradeRuleActiveNominated property={property} />
        </>
      )}
      {!APIConfig().feature(FEATURE_INHIBIT_COMMUNITY_TRADES) && (
        <TradeRuleActiveCommunity property={property} />
      )}
      <TradeRuleActiveResidual property={property} />
    </>
  );
}

TradeRuleActive.propTypes = {
  property: PropTypes.object, // eslint-disable-line react/forbid-prop-types
};

TradeRuleActive.defaultProps = {
  property: null,
};

export default TradeRuleActive;
